import Moment from 'moment-timezone'
import InsiderEnum from './InsiderEnum'
import InsiderUtils from './InsiderUtils'
import SessionEventState from './SessionEventState'
import switchDefaultCustom from './utils/switchDefaultCustom'
import cashbackToBePay from './utils/cashbackToBePay'
import HashIdGenerate from '~/services/HashIdGenerate.js'
import financialInformation from './utils/financialInformation'
import insiderSet from '~/services/Insider/sets/insiderSet.js'
import voucherDiscountInf from './utils/voucherDiscountInf.js'
import { subTotalInds } from './utils/valuePriceTractative.js'
import formatAmountWithSymbol from './utils/formatAmountWithSymbol'

export default class InsiderGlobal {
  utils = InsiderUtils.start()

  load (pageType = null) {
    this.pageType = {
      page: {
        type: pageType
      }
    }
    this.insiderEventName = pageType
    this.insiderObject = {}
    this.insiderEvent = {}
    this.currentTimezone = 'Etc/UTC'
    this.hashIdGenerate = HashIdGenerate.getInstance()
    this.insiderEnum = InsiderEnum
    this.insiderSet = insiderSet
  }

  setBasketObject (data) {
    this.insiderObject = {
      ...this.pageType,
      basket: data.basket
    }
  }

  setDefaultObject (data) {
    this.insiderObject = {
      ...data,
      ...this.pageType
    }
  }

  setListingPageObject (data) {
    this.insiderObject = {
      ...data,
      ...this.pageType
    }
    this.resetUserIO(data?.user)
  }

  sendEvent (timestamp = 'now', attempt = 0, maxAttempts = 10) {
    if (attempt >= maxAttempts) return

    if (window.Insider) {
      window.Insider.track('events', this.getFormattedInsiderEvent(timestamp))
    } else {
      setTimeout(() => {
        this.sendEvent(timestamp, ++attempt)
      }, 300)
    }
  }

  setHomeObject (custom) {
    window.insider_object = {
      page: {
        type: 'Home'
      },
      custom
    }
  }

  setCookie (key, value) {
    window.$nuxt.$cookie.set(key, value)
  }

  baseSearchObject (params) {
    return this.utils.buildSearchObject(params)
  }

  clientHasReturning () {
    const lastAccessDate = this.utils.getCookie('last_access_date')
    const amountOfDays = this.getAmountOfDays(lastAccessDate)

    this.setCookie(
      'last_access_date',
      new Moment().tz(this.currentTimezone).format('YYYY-MM-DD HH:mm:ss')
    )

    if (!lastAccessDate || amountOfDays > 365) {
      return false
    }

    return true
  }

  getAmountOfDays (timestamp = null) {
    const currentDateTime = this.utils.formatTimeStamp()
    const lastAccessDateTime = timestamp ? this.utils.formatTimeStamp(timestamp) : this.utils.formatTimeStamp()

    return currentDateTime.diff(lastAccessDateTime, 'days')
  }

  getStatusTexto (codigo) {
    switch (codigo) {
      case 17:
        return 'Pendente'
      case 18:
        return 'Capturado'
      case 20:
        return 'Cancelado'
      case 21:
        return 'Negado'
      case 22:
        return 'Autorizado'
      case 246:
        return 'Aguardando Aprovação'
      default:
        return 'Status Desconhecido'
    }
  }

  getInsiderObject () {
    return Object.assign({}, this.pageType, this.insiderObject)
  }

  getFormattedInsiderEvent (timestamp = 'now') {
    let currentTimestamp = new Moment()

    if (timestamp && timestamp !== 'now') {
      currentTimestamp = new Moment(timestamp)
    }

    const response = [
      {
        event_name: this.pageType.page.type,
        timestamp: currentTimestamp.tz(this.currentTimezone).format('YYYY-MM-DDTHH:mm:ss') + 'Z',
        event_params: switchDefaultCustom(this.insiderEvent)
      }
    ]

    if (SessionEventState.start().get()) {
      response.push(
        {
          event_name: this.insiderEnum.SESSION_START,
          timestamp: currentTimestamp.tz(this.currentTimezone).format('YYYY-MM-DDTHH:mm:ss') + 'Z',
          event_params: SessionEventState.start().get()
        }
      )
      SessionEventState.start().set(false)
    }

    return response
  }

  buildTaxonomy(configurations) {
    return configurations
      .map(e => e.position ? `${e.name}:${e.value}` : undefined)
      .filter(e => e !== undefined)
  }

  getViewdBaseObject (params) {
    const {
      itinerary,
      vehicle,
      evaluation,
      cashback
    } = params

    const payment = params.payment || params.netRateOption || params.payOnArrivalOption
    const vehicleDetails = this.buildTaxonomy(vehicle?.configurations || [])
    const locale = params.locale || params.bookingInfo.locale || this.utils.getLocale()

    const response = {
      product_image_url: `https:${process.env.RENTCARS_CDN.replace('https:', '')}${vehicle.image}`,
      ...this.utils.mainItineraryInformation(itinerary, params.user),
      rental_length: itinerary.daily,
      days_in_advance: itinerary.daysadvance,
      language: this.utils.insiderFormatLanguage(locale.toLowerCase()),
      supplier_name: params.rentalCompany.brand.replace(' Rent a Car', ''),
      product: vehicle.code,
      id: vehicle.id,
      name: vehicle.name,
      taxonomy: vehicleDetails,
      rental_company_evaluation: evaluation?.index?.value,
      quantity: this.insiderEnum.QUANTITY_BOOKING,
      optionals: this.utils.getAdditionalValues(params.extra, params.additionalServices, this.insiderEnum.INCLUDED),
      url: this.utils.getUrl(),
      unit_price: payment.totalAmount.value,
      unit_sale_price: payment.totalAmountDiscount?.value || payment.totalAmount?.value,
      currency: payment.totalAmount.currency,
      device_type: InsiderEnum.MOBILE,
      promotion_name: payment?.tag?.name ? payment?.tag?.name : null
    }

    const cashbackType = cashbackToBePay(cashback, this.insiderEnum.CASHBACK_TYPE_VERIFY)

    if (cashbackType) {
      response.type = cashbackType
    }

    return response
  }

  handlerUserInfs (params) {
    const userData = params.userData
    const utmObject = params.utmObject ?? (params.utm ?? {})

    if (!userData) {
      return {}
    }

    return this.utils.userInfs(userData, utmObject, params.requestorId, this.clientHasReturning())
  }

  toSessionsEvent (params) {
    const paramsToSend = {}
    let numberOfAttr = 0
    let numberOfAttrIsZero = 0
    let utms
    let propertyUtm

    if (paramsToSend.utmObject) {
      utms = paramsToSend.utmObject
      propertyUtm = 'utmObject'
    } else if (paramsToSend.utm) {
      utms = paramsToSend.utm
      propertyUtm = 'utm'
    } else {
      utms = {}
      propertyUtm = ''
    }

    const ruleUtm = ['utm', 'utmObject'].includes(propertyUtm)

    if (ruleUtm) {
      for (const property in utms) {
        numberOfAttr += 1
        if (!utms[property]) {
          paramsToSend[property] = params[property]
          numberOfAttrIsZero += 1
        }
      }
    }

    this.insiderEvent = {
      requestor_id: params.requestorId,
      device_type: InsiderEnum.MOBILE
    }

    if (ruleUtm && (numberOfAttr !== numberOfAttrIsZero)) {
      this.insiderEvent[propertyUtm] = utms
    }
  }

  builderObjectOfFinancialInformation (params) {
    const paymentFormatted = financialInformation(params, this.insiderEnum.GET_TOTAL_FORMATTED_VALUE)

    return {
      currency: financialInformation(params, this.insiderEnum.GET_CURRENCY),
      subtotal: financialInformation(params, this.insiderEnum.TOTAL_VLW_WITHOUT_RENTALCOVER),
      payment_currency: financialInformation(params, this.insiderEnum.GET_CURRENCY),
      payment_value: financialInformation(params, this.insiderEnum.GET_TOTAL_VALUE),
      payment_formatted_amount: `${paymentFormatted} ${params.extra?.totalAmount?.currency}`,
      payment_amount: parseFloat((subTotalInds(params) - params.extra?.totalAmount?.value ?? 0).toFixed(2)),
      formatted_amount: formatAmountWithSymbol(params)
    }
  }

  getInsiderEvent () {
    return this.insiderEvent
  }

  setViewedSteps (params) {
    const payment = params.payment || params.netRateOption || params.payOnArrivalOption
    this.insiderEvent = {
      promotion_name: payment?.tag?.name ? payment?.tag?.name : null,
      plan: params.plan.name,
      type: payment.type,
      language: this.utils.insiderFormatLanguage(params?.locale),
      user: params.user,
      device_type: InsiderEnum.MOBILE,
      referrer: document.referrer || null,
      ...this.builderObjectOfFinancialInformation(params),
      ...this.getViewdBaseObject(params)
    }
  }

  _adapterInsiderElement (params, functionName) {
    this[functionName](params)

    return this.insiderEvent
  }

  setPurchase (params) {
    params = Object.assign(params, {})
    const { booking, paymentType } = params

    const argViewedStep = {
      ...booking,
      user: params.user
    }

    this.insiderEvent = {
      ...this.insiderEvent,
      payment_type: paymentType,
      order_id: booking?.bookingInfo?.id,
      order_code: booking?.bookingInfo?.code,
      event_group_id: booking?.bookingInfo?.id,
      payment: paymentType === this.insiderEnum.PAY_AT_DESTINATION ? this.insiderEnum.PD : booking?.payment?.type,
      language: this.utils.insiderFormatLanguage(params?.locale),
      device_type: InsiderEnum.MOBILE,
      referrer: document.referrer || null,
      ...this.setViewedSteps(argViewedStep),
      ...voucherDiscountInf(params),
      ...this.getViewdBaseObject(booking),
      ...this.builderObjectOfFinancialInformation(params)
    }
  }

  resetUserIO (user) {
    if (!user && window.insider_object) {
      delete window.insider_object.user
    }
  }
}
